import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ModalDirective, ModalModule } from 'ngx-bootstrap/modal';
import { AnnualDetailAssessment } from '../Annual-Year/annual-year.model';
import { Appeal, AppealLevel } from '../Appeals/appeal.model';
import { AppealService } from '../Appeals/appeal.service';
import { ReferralsService } from '../../Litigation/Referral/referrals.service';
import { FormsModule } from '@angular/forms';
import { ModalWrapperComponent } from '../../UI-Lib/Modal-Wrapper/modalWrapper.component';

@Component({
    selector: 'add-appeal-modal',
    templateUrl: './add-appeal.component.html',
    imports: [FormsModule, ModalModule, ModalWrapperComponent],
})
export class AddAppealModalComponent {
    constructor(
        private appealService: AppealService,
        private readonly _referralsService: ReferralsService,
    ) {
        this.addAppeal = new EventEmitter();
    }

    @Input() assessments: AnnualDetailAssessment[];
    @Input() appealLevels: AppealLevel[];
    @Input() selectedAnnualAssessmentId: number;
    @Input() topLevelCompanyId: number;
    @Output() addAppeal: EventEmitter<Appeal>;
    @ViewChild('addAppealModal', { static: true }) addAppealModal: ModalDirective;

    newAppeal: Appeal;
    loading: boolean;
    isAppealLevelLitigation: boolean = false;
    addLitigationReferralTask: boolean = false;
    shouldShowLitigationReferralTask: boolean = false;

    private _litigationReferralsEnabled: boolean = false;

    show(): void {
        this.addAppealModal.show();
    }

    async showModalInit(): Promise<void> {
        this.newAppeal = new Appeal();
        this.isAppealLevelLitigation = false;
        this.addLitigationReferralTask = false;
        this.shouldShowLitigationReferralTask = false;
        this._litigationReferralsEnabled = false;

        if (this.appealLevels && this.appealLevels.length > 0) {
            this.newAppeal.appealLevelID = this.appealLevels[0].appealLevelID;
        }

        if (this.assessments && this.assessments.length > 0) {
            const selectedAssessment = this.assessments.find(
                (assessment) => assessment.annualAssessmentID === this.selectedAnnualAssessmentId,
            );
            const firstAssessment = this.assessments.reduce((earliestAssessment, assessment) =>
                assessment.revisionNum < earliestAssessment.revisionNum ? assessment : earliestAssessment,
            );
            const defaultAssessment = selectedAssessment ?? firstAssessment;

            this.newAppeal.annualAssessmentID = defaultAssessment.annualAssessmentID;
        }

        if (this.topLevelCompanyId) {
            try {
                this._litigationReferralsEnabled = await this._referralsService.areLitigationReferralsAllowedForCompany(
                    this.topLevelCompanyId,
                );
            } catch {
                this._litigationReferralsEnabled = false;
            }
        }

        this._updateLitigationReferralTask();
    }

    onAppealLevelChange(): void {
        this._updateLitigationReferralTask();
    }

    async save(): Promise<void> {
        this.loading = true;

        try {
            const appeal = await this.appealService.createAppeal(this.newAppeal, this.addLitigationReferralTask);
            this.addAppeal.emit(appeal);
            this.addAppealModal.hide();
        } finally {
            this.loading = false;
        }
    }

    private _updateLitigationReferralTask(): void {
        const selectedLevel = this.appealLevels?.find((l) => l.appealLevelID === this.newAppeal?.appealLevelID);
        this.isAppealLevelLitigation = !!selectedLevel?.litigation;
        const canShowLitigationReferralTask = this._litigationReferralsEnabled && this.isAppealLevelLitigation;
        this.shouldShowLitigationReferralTask = canShowLitigationReferralTask;
        this.addLitigationReferralTask = canShowLitigationReferralTask;
    }
}
